import React, { useState, useEffect } from 'react';
import { User } from '../types';

interface FavoritesPanelProps {
  currentUser: User;
  onUserClick: (user: User) => void;
  onFavoriteToggle: (userId: string) => void;
  onCallRequest?: (targetUserId: string) => void;
  canCallRequest?: (targetUserId: string) => boolean;
  isFavorite: (userId: string) => boolean;
}

const FavoritesPanel: React.FC<FavoritesPanelProps> = ({
  currentUser,
  onUserClick,
  onFavoriteToggle,
  onCallRequest,
  canCallRequest,
  isFavorite
}) => {
  const [favorites, setFavorites] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const API_URL = 'https://voicechatapi.kasaneko.com';

  useEffect(() => {
    loadFavorites();
  }, [currentUser.userId]);

  const loadFavorites = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_URL}/api/users/favorites/${currentUser.userId}`);
      const data = await response.json();

      if (data.success) {
        const favoriteUsers: User[] = data.favorites.map((f: any) => ({
          userId: f.userId,
          userCode: f.userCode,
          username: f.username,
          profileImage: f.profileImage,
          gender: f.gender,
          hobbies: f.hobbies,
          bio: f.bio,
          position: f.position || { x: 0, y: 0, z: 0 },
          isOnline: f.isOnline,
          avatar: f.avatar
        }));
        setFavorites(favoriteUsers);
      } else {
        setError('お気に入り一覧の取得に失敗しました');
      }
    } catch (error) {
      console.error('お気に入り取得エラー:', error);
      setError('お気に入り一覧の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleFavoriteToggle = async (userId: string) => {
    try {
      const response = await fetch(`${API_URL}/api/users/favorites/toggle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: currentUser.userId,
          targetUserId: userId
        })
      });

      const data = await response.json();

      if (data.success) {
        // 一覧を更新
        await loadFavorites();
      } else {
        alert(data.error || 'お気に入りの更新に失敗しました');
      }
    } catch (error) {
      console.error('お気に入り更新エラー:', error);
      alert('お気に入りの更新に失敗しました');
    }
  };

  if (loading) {
    return (
      <div style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        color: '#333',
        fontSize: '18px',
        background: 'linear-gradient(135deg, #f0f4ff 0%, #e8f0fe 50%, #dde9ff 100%)'
      }}>
        読み込み中...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        color: '#333',
        fontSize: '16px',
        padding: '20px',
        textAlign: 'center',
        background: 'linear-gradient(135deg, #f0f4ff 0%, #e8f0fe 50%, #dde9ff 100%)'
      }}>
        {error}
      </div>
    );
  }

  return (
    <div style={{
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      padding: '20px',
      overflow: 'auto',
      background: 'linear-gradient(135deg, #f0f4ff 0%, #e8f0fe 50%, #dde9ff 100%)'
    }}>
      <h2 style={{
        color: '#333',
        fontSize: '24px',
        fontWeight: 'bold',
        marginBottom: '20px',
        textAlign: 'center'
      }}>
        お気に入り
      </h2>

      {selectedUser ? (
        <div style={{
          padding: '20px',
          height: '100%',
          overflow: 'auto'
        }}>
          <button
            onClick={() => setSelectedUser(null)}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: '20px',
              marginBottom: '15px',
              color: '#333'
            }}
          >
            ← 戻る
          </button>
          <div style={{
            background: 'rgba(255, 255, 255, 0.95)',
            borderRadius: '15px',
            padding: '25px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            position: 'relative'
          }}>
            {/* お気に入りボタン（右上） */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onFavoriteToggle(selectedUser.userId);
              }}
              style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontSize: '32px',
                padding: '5px',
                lineHeight: '1',
                zIndex: 10
              }}
              title={isFavorite(selectedUser.userId) ? 'お気に入り解除' : 'お気に入り追加'}
            >
              {isFavorite(selectedUser.userId) ? '❤️' : '🤍'}
            </button>

            {/* プロフィール画像 */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '20px'
            }}>
              <div style={{
                width: '120px',
                height: '120px',
                borderRadius: '50%',
                background: selectedUser.profileImage
                  ? `url(${selectedUser.profileImage}) center/cover`
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '48px',
                border: '4px solid #667eea',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
              }}>
                {!selectedUser.profileImage && '👤'}
              </div>
            </div>

            {/* ユーザー名 */}
            <h3 style={{
              textAlign: 'center',
              fontSize: '22px',
              fontWeight: 'bold',
              color: '#333',
              marginBottom: '10px'
            }}>
              {selectedUser.username}
            </h3>

            {/* ユーザーID */}
            {selectedUser.userCode && (
              <div style={{
                textAlign: 'center',
                fontSize: '14px',
                color: '#666',
                marginBottom: '20px'
              }}>
                ID: {selectedUser.userCode}
              </div>
            )}

            {/* 性別 */}
            {selectedUser.gender && (
              <div style={{
                marginBottom: '15px',
                paddingBottom: '15px',
                borderBottom: '1px solid rgba(0, 0, 0, 0.1)'
              }}>
                <div style={{
                  fontSize: '12px',
                  color: '#666',
                  marginBottom: '5px'
                }}>
                  性別
                </div>
                <div style={{
                  fontSize: '16px',
                  color: '#333'
                }}>
                  {selectedUser.gender}
                </div>
              </div>
            )}

            {/* 趣味 */}
            {selectedUser.hobbies && (
              <div style={{
                marginBottom: '15px',
                paddingBottom: '15px',
                borderBottom: '1px solid rgba(0, 0, 0, 0.1)'
              }}>
                <div style={{
                  fontSize: '12px',
                  color: '#666',
                  marginBottom: '5px'
                }}>
                  趣味
                </div>
                <div style={{
                  fontSize: '16px',
                  color: '#333',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}>
                  {selectedUser.hobbies}
                </div>
              </div>
            )}

            {/* 自己紹介 */}
            {selectedUser.bio && (
              <div style={{
                marginBottom: '20px'
              }}>
                <div style={{
                  fontSize: '12px',
                  color: '#666',
                  marginBottom: '5px'
                }}>
                  自己紹介
                </div>
                <div style={{
                  fontSize: '16px',
                  color: '#333',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  lineHeight: '1.6'
                }}>
                  {selectedUser.bio}
                </div>
              </div>
            )}

            {/* 通話依頼ボタン（プロフィール内の下） */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (onCallRequest && canCallRequest && canCallRequest(selectedUser.userId)) {
                  onCallRequest(selectedUser.userId);
                } else if (onUserClick) {
                  onUserClick(selectedUser);
                }
              }}
              disabled={onCallRequest && canCallRequest && !canCallRequest(selectedUser.userId)}
              style={{
                width: '100%',
                padding: '15px',
                background: (onCallRequest && canCallRequest && !canCallRequest(selectedUser.userId)) ? '#ccc' : '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: (onCallRequest && canCallRequest && !canCallRequest(selectedUser.userId)) ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                marginTop: '25px'
              }}
            >
              <span>⭐</span>
              {onCallRequest && canCallRequest
                ? (canCallRequest(selectedUser.userId) ? '通話依頼' : '通話依頼（利用不可）')
                : '通話依頼'}
            </button>
          </div>
        </div>
      ) : favorites.length === 0 ? (
        <div style={{
          textAlign: 'center',
          color: '#666',
          fontSize: '16px',
          marginTop: '40px'
        }}>
          お気に入りユーザーはいません
        </div>
      ) : (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '15px'
        }}>
          {favorites.map((favorite) => (
            <div
              key={favorite.userId}
              onClick={() => setSelectedUser(favorite)}
              style={{
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '12px',
                padding: '15px',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                transition: 'transform 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: '15px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.02)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              {/* アイコン */}
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: favorite.profileImage
                  ? `url(${favorite.profileImage}) center/cover`
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '24px',
                flexShrink: 0,
                border: '2px solid #667eea'
              }}>
                {!favorite.profileImage && '👤'}
              </div>

              {/* ユーザー情報 */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: '16px',
                  fontWeight: 'bold',
                  color: '#333',
                  marginBottom: '5px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {favorite.username}
                </div>
                {favorite.userCode && (
                  <div style={{
                    fontSize: '12px',
                    color: '#666',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    ID: {favorite.userCode}
                  </div>
                )}
              </div>

              {/* お気に入りボタン */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleFavoriteToggle(favorite.userId);
                }}
                style={{
                  background: isFavorite(favorite.userId) ? '#ff69b4' : '#ccc',
                  border: 'none',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  fontSize: '20px',
                  flexShrink: 0
                }}
                title={isFavorite(favorite.userId) ? 'お気に入り解除' : 'お気に入り追加'}
              >
                {isFavorite(favorite.userId) ? '❤️' : '🤍'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FavoritesPanel;

